import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Badge, Checkbox, Stack,
  Alert, LinearProgress, List, ListItem, ListItemText
} from '@mui/material';
import {
  School as SchoolIcon,
  Devices as DevicesIcon,
  Payment as PaymentIcon,
  Support as SupportIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  FileDownload as FileDownloadIcon,
  AssignmentTurnedIn as AssignmentIcon
} from '@mui/icons-material';
import './AdminDashboard.css';
import DeviceDebugReport from './DeviceDebugReport';
import DeviceCompleteSetup from './DeviceCompleteSetup';
import MarketingDeviceAssign from './MarketingDeviceAssign';
import AdminBillingReports from './AdminBillingReports';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const SuperAdminDashboard = ({ user, onLogout }) => {
  const [activeMenu, setActiveMenu] = useState('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({ devices: false });
  const [schools, setSchools] = useState([]);
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [schoolDialog, setSchoolDialog] = useState(false);
  const [viewSchoolDialog, setViewSchoolDialog] = useState(false);
  const [userDialog, setUserDialog] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [schoolForm, setSchoolForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    subscription_plan: 'basic'
  });
  const [userForm, setUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'school',
    school_id: '',
    password: '',
    children_ids: []
  });
  const [students, setStudents] = useState([]);
  const [userFilter, setUserFilter] = useState('school');
  const [schoolSearch, setSchoolSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('all');
  const [deviceStatusFilter, setDeviceStatusFilter] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [deviceDialog, setDeviceDialog] = useState(false);
  const [assignDeviceDialog, setAssignDeviceDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceForm, setDeviceForm] = useState({
    device_id: '',
    type: 'GPS Tracker',
    imei_number: '',
    sim_number: '',
    model: '',
    manufacturer: ''
  });
  const [assignForm, setAssignForm] = useState({
    school_id: ''
  });
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadSummary, setBulkUploadSummary] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkAssignFile, setBulkAssignFile] = useState(null);
  const [bulkAssignSummary, setBulkAssignSummary] = useState(null);
  const [bulkAssignUploading, setBulkAssignUploading] = useState(false);
  const [bulkAssignError, setBulkAssignError] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [assignToAgencyDialog, setAssignToAgencyDialog] = useState(false);
  const [bulkAssignToAgencyDialog, setBulkAssignToAgencyDialog] = useState(false);
  const [bulkAssignToAgencyFile, setBulkAssignToAgencyFile] = useState(null);
  const [bulkAssignToAgencyId, setBulkAssignToAgencyId] = useState('');
  const [bulkAssignToAgencySummary, setBulkAssignToAgencySummary] = useState(null);
  const [bulkAssignToAgencyUploading, setBulkAssignToAgencyUploading] = useState(false);
  const [bulkAssignToAgencyError, setBulkAssignToAgencyError] = useState('');
  const [assignToAgencyForm, setAssignToAgencyForm] = useState({
    agency_id: ''
  });
  const [agencyDialog, setAgencyDialog] = useState(false);
  const [isEditingAgency, setIsEditingAgency] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyForm, setAgencyForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    contact_person: '',
    commission_type: 'percentage',
    sales_commission: '',
    status: 'active',
    password: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (user && user.id && token) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        return;
      }
      
      const schoolsResponse = await fetch(`${API_BASE_URL}/admin/schools`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!schoolsResponse.ok) {
        return;
      }
      
      const schoolsData = await schoolsResponse.json();
      setSchools(Array.isArray(schoolsData) ? schoolsData : []);
      
      const devicesResponse = await fetch(`${API_BASE_URL}/admin/devices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const devicesData = await devicesResponse.json();
      setDevices(Array.isArray(devicesData) ? devicesData : []);
      
      const analyticsResponse = await fetch(`${API_BASE_URL}/admin/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData || {});
      
      const usersResponse = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const usersData = await usersResponse.json();
      setUsers(Array.isArray(usersData) ? usersData : []);

      const agenciesResponse = await fetch(`${API_BASE_URL}/admin/agencies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const agenciesData = await agenciesResponse.json();
      setAgencies(Array.isArray(agenciesData) ? agenciesData : []);
    } catch (error) {
      setSchools([]);
      setDevices([]);
      setUsers([]);
      setAnalytics({});
      setAgencies([]);
    } finally {
      setLoading(false);
    }
  };

  const resetBulkAssignState = () => {
    setBulkAssignFile(null);
    setBulkAssignSummary(null);
    setBulkAssignError('');
    setBulkAssignUploading(false);
  };

  const resetBulkAssignToAgencyState = () => {
    setBulkAssignToAgencyFile(null);
    setBulkAssignToAgencyId('');
    setBulkAssignToAgencySummary(null);
    setBulkAssignToAgencyError('');
    setBulkAssignToAgencyUploading(false);
  };

  const handleBulkAssignDialogClose = () => {
    setBulkAssignDialog(false);
    resetBulkAssignState();
  };

  const handleBulkAssignToAgencyDialogClose = () => {
    setBulkAssignToAgencyDialog(false);
    resetBulkAssignToAgencyState();
  };

  const handleBulkAssignFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setBulkAssignFile(file);
    setBulkAssignSummary(null);
    setBulkAssignError('');
  };

  const handleBulkAssignToAgencyFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setBulkAssignToAgencyFile(file);
    setBulkAssignToAgencySummary(null);
    setBulkAssignToAgencyError('');
  };

  const handleDownloadAssignmentSample = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/admin/devices/assignments/sample-excel`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to download sample file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'device_assignment_sample.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setBulkAssignError(error.message || 'Unable to download sample file');
    }
  };

  const handleDownloadAgencyAssignmentSample = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/admin/devices/agency-assignments/sample-excel`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to download sample file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'admin_device_ids_sample.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setBulkAssignToAgencyError(error.message || 'Unable to download sample file');
    }
  };

  const handleBulkAssignmentUpload = async () => {
    if (!bulkAssignFile) {
      setBulkAssignError('Please select an Excel file to upload.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      setBulkAssignUploading(true);
      setBulkAssignError('');

      const formData = new FormData();
      formData.append('file', bulkAssignFile);

      const response = await fetch(`${API_BASE_URL}/admin/devices/assignments/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to assign devices');
      }

      setBulkAssignSummary(data);
      await loadDashboardData();
    } catch (error) {
      setBulkAssignError(error.message || 'Bulk assignment failed');
    } finally {
      setBulkAssignUploading(false);
    }
  };

  const handleBulkAssignToAgencyUpload = async () => {
    if (!bulkAssignToAgencyId) {
      setBulkAssignToAgencyError('Please select an agency.');
      return;
    }

    if (!bulkAssignToAgencyFile) {
      setBulkAssignToAgencyError('Please select an Excel file to upload.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      setBulkAssignToAgencyUploading(true);
      setBulkAssignToAgencyError('');

      const formData = new FormData();
      formData.append('file', bulkAssignToAgencyFile);
      formData.append('agency_id', bulkAssignToAgencyId);

      const response = await fetch(`${API_BASE_URL}/admin/devices/agency-assignments/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to assign devices to agency');
      }

      setBulkAssignToAgencySummary(data);
      await loadDashboardData();
    } catch (error) {
      setBulkAssignToAgencyError(error.message || 'Bulk assignment to agency failed');
    } finally {
      setBulkAssignToAgencyUploading(false);
    }
  };

  const handleCreateAgency = () => {
    setIsEditingAgency(false);
    setSelectedAgency(null);
    setAgencyForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      contact_person: '',
      commission_type: 'percentage',
      sales_commission: '',
      status: 'active',
      password: ''
    });
    setAgencyDialog(true);
  };

  const handleEditAgency = (agency) => {
    setIsEditingAgency(true);
    setSelectedAgency(agency);
    setAgencyForm({
      name: agency.name,
      email: agency.email,
      phone: agency.phone || '',
      address: agency.address || '',
      contact_person: agency.contact_person || '',
      commission_type: agency.commission_type || 'percentage',
      sales_commission: agency.sales_commission || '',
      status: agency.status || 'active',
      password: ''
    });
    setAgencyDialog(true);
  };

  const handleSaveAgency = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      // Validate commission against default
      const defaultCommission = parseFloat(process.env.REACT_APP_AGENCY_DEFAULT_COMMISSION) || 5;
      const commissionValue = parseFloat(agencyForm.sales_commission);

      if (commissionValue > defaultCommission) {
        alert(`Sales commission cannot exceed agency default commission of ${defaultCommission}%`);
        return;
      }

      const url = isEditingAgency
        ? `${API_BASE_URL}/admin/agencies/${selectedAgency.id}`
        : `${API_BASE_URL}/admin/agencies`;

      const method = isEditingAgency ? 'PUT' : 'POST';

      const payload = { ...agencyForm };
      if (!isEditingAgency || !payload.password?.trim()) {
        delete payload.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save agency');
      }

      alert(data.message);
      setAgencyDialog(false);
      await loadDashboardData();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderOverview = () => (
    <Box>
      <Typography className="admin-page-title" variant="h5" gutterBottom>
        Overview
      </Typography>
      <Typography className="admin-page-desc">
        System-wide snapshot of schools, devices, and activity.
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Schools"
            value={analytics.totalSchools || 0}
            icon={<SchoolIcon />}
            color="#0f766e"
            subtitle="Active subscriptions"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Devices"
            value={analytics.totalDevices || 0}
            icon={<DevicesIcon />}
            color="#115e59"
            subtitle={`${analytics.activeDevices || 0} active`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Revenue"
            value={`₹${analytics.monthlyRevenue || 0}`}
            icon={<PaymentIcon />}
            color="#0b5f59"
            subtitle="This month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Open Tickets"
            value={analytics.openTickets || 0}
            icon={<SupportIcon />}
            color="#134e4a"
            subtitle="Pending resolution"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Schools
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>School Name</TableCell>
                      <TableCell>Students</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(schools) && schools.slice(0, 5).map((school) => (
                      <TableRow key={school.id}>
                        <TableCell>{school.name}</TableCell>
                        <TableCell>{school.total_students}</TableCell>
                        <TableCell>
                          <Chip 
                            label={school.status} 
                            color={school.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Device Inventory
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Device ID</TableCell>
                      <TableCell>School</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(devices) && devices.slice(0, 5).map((device) => (
                      <TableRow key={device.id}>
                        <TableCell>{device.id}</TableCell>
                        <TableCell>{device.school_name || 'Unassigned'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={device.assignment_status}
                            color={device.assignment_status === 'assigned' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const handleCreateSchool = () => {
    setIsEditing(false);
    setSchoolForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      subscription_plan: 'basic'
    });
    setSchoolDialog(true);
  };

  const handleEditSchool = (school) => {
    setIsEditing(true);
    setSelectedSchool(school);
    setSchoolForm({
      name: school.name,
      email: school.email || '',
      phone: school.phone || '',
      address: school.address || '',
      subscription_plan: school.plan_name?.toLowerCase() || 'basic'
    });
    setSchoolDialog(true);
  };

  const handleViewSchool = (school) => {
    setSelectedSchool(school);
    setViewSchoolDialog(true);
  };

  const handleSaveSchool = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        return;
      }
      
      if (isEditing) {
        const response = await fetch(`${API_BASE_URL}/admin/schools/${selectedSchool.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(schoolForm)
        });
        
        if (!response.ok) {
          return;
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/admin/schools`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(schoolForm)
        });
        
        if (!response.ok) {
          return;
        }
      }
      
      await loadDashboardData();
      setSchoolDialog(false);
    } catch (error) {
      // Handle error silently
    }
  };

  const renderSchoolManagement = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography className="admin-page-title" variant="h5">
          Schools
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search schools..."
            value={schoolSearch}
            onChange={(e) => setSchoolSearch(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateSchool}>
            Add School
          </Button>
        </Box>
      </Box>
      
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>School Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Students</TableCell>
                <TableCell>Devices</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(filteredSchools) && filteredSchools.map((school) => (
                <TableRow key={school.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {school.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{school.email}</TableCell>
                  <TableCell>{school.total_students}</TableCell>
                  <TableCell>{school.total_devices}</TableCell>
                  <TableCell>{school.plan_name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={school.status}
                      color={school.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleViewSchool(school)}>
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEditSchool(school)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create/Edit School Dialog */}
      <Dialog open={schoolDialog} onClose={() => setSchoolDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit School' : 'Create New School'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="School Name"
                value={schoolForm.name}
                onChange={(e) => setSchoolForm({...schoolForm, name: e.target.value})}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={schoolForm.email}
                onChange={(e) => setSchoolForm({...schoolForm, email: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={schoolForm.phone}
                onChange={(e) => setSchoolForm({...schoolForm, phone: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={schoolForm.address}
                onChange={(e) => setSchoolForm({...schoolForm, address: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Subscription Plan</InputLabel>
                <Select
                  value={schoolForm.subscription_plan}
                  onChange={(e) => setSchoolForm({...schoolForm, subscription_plan: e.target.value})}
                  label="Subscription Plan"
                >
                  <MenuItem value="basic">Basic Plan</MenuItem>
                  <MenuItem value="standard">Standard Plan</MenuItem>
                  <MenuItem value="premium">Premium Plan</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchoolDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveSchool} variant="contained">
            {isEditing ? 'Update School' : 'Create School'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View School Dialog */}
      <Dialog open={viewSchoolDialog} onClose={() => setViewSchoolDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>School Details</DialogTitle>
        <DialogContent>
          {selectedSchool && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">School Name</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedSchool.name}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{selectedSchool.email || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                <Typography variant="body1">{selectedSchool.phone || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                <Typography variant="body1">{selectedSchool.address || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">Total Students</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedSchool.total_students}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">Total Devices</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedSchool.total_devices}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">Subscription Plan</Typography>
                <Typography variant="body1">{selectedSchool.plan_name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Chip 
                  label={selectedSchool.status}
                  color={selectedSchool.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSchoolDialog(false)}>Close</Button>
          <Button 
            onClick={() => {
              setViewSchoolDialog(false);
              handleEditSchool(selectedSchool);
            }} 
            variant="contained"
          >
            Edit School
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const handleAddDevice = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/devices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(deviceForm)
      });
      
      if (response.ok) {
        await loadDashboardData();
        setDeviceDialog(false);
        setDeviceForm({
          device_id: '',
          type: 'GPS Tracker',
          imei_number: '',
          sim_number: '',
          model: '',
          manufacturer: ''
        });
      }
    } catch (error) {
      // Handle error
    }
  };

  const handleAssignDevice = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/devices/${selectedDevice.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(assignForm)
      });
      
      if (response.ok) {
        await loadDashboardData();
        setAssignDeviceDialog(false);
        setAssignForm({ school_id: '' });
      }
    } catch (error) {
      // Handle error
    }
  };

  const handleUnassignDevice = async (device) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/devices/${device.id}/unassign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        await loadDashboardData();
      }
    } catch (error) {
      // Handle error
    }
  };

  const handleDeleteDevice = async (device) => {
    try {
      if (!device) return;
      const deviceId = device.device_id || device.id;
      if (!deviceId) return;

      if (!window.confirm(`Are you sure you want to delete device ${deviceId}? This action cannot be undone.`)) {
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/admin/devices/${deviceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json().catch(() => null);

      if (response.ok) {
        alert((data && data.message) || 'Device deleted successfully');
        await loadDashboardData();
        return;
      }

      if (data && Array.isArray(data.assigned_students) && data.assigned_students.length > 0) {
        const students = data.assigned_students
          .map((s) => (s && s.name ? s.name : s && s.id ? `#${s.id}` : 'Unknown'))
          .join(', ');
        alert(`${data.message || 'Cannot delete device.'}\nAssigned students: ${students}`);
        return;
      }

      alert(`Error: ${(data && data.message) || 'Failed to delete device'}`);
    } catch (error) {
      console.error('Error deleting device:', error);
      alert('Network error: ' + error.message);
    }
  };

  const resetDeviceForm = () => {
    setDeviceForm({
      device_id: '',
      type: 'GPS Tracker',
      imei_number: '',
      sim_number: '',
      model: '',
      manufacturer: ''
    });
  };

  const resetBulkUploadState = () => {
    setBulkUploadFile(null);
    setBulkUploadSummary(null);
    setBulkUploadError('');
    setBulkUploading(false);
  };

  const handleBulkDialogClose = () => {
    setBulkUploadDialog(false);
    resetBulkUploadState();
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setBulkUploadFile(file);
    setBulkUploadSummary(null);
    setBulkUploadError('');
  };

  const handleDownloadSampleDevices = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/admin/devices/sample-excel`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to download sample file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'device_import_sample.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setBulkUploadError(error.message || 'Unable to download sample file');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      setBulkUploadError('Please select an Excel file to upload.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      setBulkUploading(true);
      setBulkUploadError('');

      const formData = new FormData();
      formData.append('file', bulkUploadFile);

      const response = await fetch(`${API_BASE_URL}/admin/devices/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload devices');
      }

      setBulkUploadSummary(data);
      await loadDashboardData();
    } catch (error) {
      setBulkUploadError(error.message || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const renderDeviceInventory = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography className="admin-page-title" variant="h5">
          Device Inventory
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetDeviceForm();
              setDeviceDialog(true);
            }}
          >
            Add Device
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => {
              resetBulkUploadState();
              setBulkUploadDialog(true);
            }}
          >
            Upload Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssignmentIcon />}
            onClick={() => {
              resetBulkAssignState();
              setBulkAssignDialog(true);
            }}
          >
            Assign via Excel
          </Button>
        </Stack>
      </Box>
      
      <Card sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Device ID, IMEI, or School"
              value={deviceSearch}
              onChange={(e) => setDeviceSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Device Type</InputLabel>
              <Select
                value={deviceTypeFilter}
                label="Device Type"
                onChange={(e) => setDeviceTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="GPS Tracker">GPS Tracker</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Assignment</InputLabel>
              <Select
                value={deviceStatusFilter}
                label="Assignment"
                onChange={(e) => setDeviceStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setDeviceSearch('');
                setDeviceTypeFilter('all');
                setDeviceStatusFilter('all');
              }}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredDevices.length} of {devices.length} devices
          </Typography>
        </Box>
      </Card>
      
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>IMEI</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Assignment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(filteredDevices) && filteredDevices.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {device.device_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{device.type}</TableCell>
                  <TableCell>{device.imei_number || 'N/A'}</TableCell>
                  <TableCell>{device.school_name || 'Unassigned'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={device.assignment_status}
                      color={
                        device.assignment_status === 'assigned' ? 'success' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={device.status}
                      color={device.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {device.assignment_status === 'unassigned' ? (
                      <>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            setSelectedDevice(device);
                            setAssignDeviceDialog(true);
                          }}
                        >
                          Assign
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDevice(device)}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          color="error"
                          onClick={() => handleUnassignDevice(device)}
                        >
                          Unassign
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDevice(device)}
                          disabled
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add Device Dialog */}
      <Dialog open={deviceDialog} onClose={() => setDeviceDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Device</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Device ID"
                value={deviceForm.device_id}
                onChange={(e) => setDeviceForm({...deviceForm, device_id: e.target.value})}
                helperText="Optional. Will default to IMEI if left blank."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Device Type</InputLabel>
                <Select
                  value={deviceForm.type}
                  onChange={(e) => setDeviceForm({...deviceForm, type: e.target.value})}
                  label="Device Type"
                >
                  <MenuItem value="GPS Tracker">GPS Tracker</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IMEI Number"
                value={deviceForm.imei_number}
                onChange={(e) => {
                  const imeiNumber = e.target.value;
                  setDeviceForm((prev) => ({
                    ...prev,
                    imei_number: imeiNumber,
                    device_id: prev.device_id || imeiNumber
                  }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SIM Number"
                value={deviceForm.sim_number}
                onChange={(e) => setDeviceForm({...deviceForm, sim_number: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                value={deviceForm.model}
                onChange={(e) => setDeviceForm({...deviceForm, model: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={deviceForm.manufacturer}
                onChange={(e) => setDeviceForm({...deviceForm, manufacturer: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeviceDialog(false)}>Cancel</Button>
          <Button onClick={handleAddDevice} variant="contained">
            Add Device
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Device Dialog */}
      <Dialog open={assignDeviceDialog} onClose={() => setAssignDeviceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Device to School</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Device: {selectedDevice?.device_id}
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Select School</InputLabel>
              <Select
                value={assignForm.school_id}
                onChange={(e) => setAssignForm({...assignForm, school_id: e.target.value})}
                label="Select School"
              >
                {schools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDeviceDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignDevice} variant="contained">
            Assign Device
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadDialog} onClose={handleBulkDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Devices</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Upload an Excel (.xlsx / .xls) file to add devices to inventory.
            Columns: <strong>device_id</strong>, <strong>imei_number</strong>, <strong>sim_number</strong>, <strong>type</strong>, <strong>model</strong>, <strong>manufacturer</strong>.
            School assignment is not part of this upload — use <strong>Assign via Excel</strong> for that.
          </Typography>

          <Stack spacing={2} mt={2}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadSampleDevices}
              disabled={bulkUploading}
            >
              Download Sample Excel
            </Button>

            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
              disabled={bulkUploading}
            >
              {bulkUploadFile ? 'Change File' : 'Select Excel File'}
              <input
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={handleBulkFileChange}
              />
            </Button>

            {bulkUploadFile && (
              <Alert severity="info">
                Selected file: <strong>{bulkUploadFile.name}</strong>
              </Alert>
            )}

            {bulkUploading && <LinearProgress />}

            {bulkUploadError && (
              <Alert severity="error" onClose={() => setBulkUploadError('')}>
                {bulkUploadError}
              </Alert>
            )}

            {bulkUploadSummary && (
              <Alert severity="success">
                <Typography variant="subtitle2">Upload Summary</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary={`Total Rows: ${bulkUploadSummary.total_rows || 0}`} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={`Inserted: ${bulkUploadSummary.inserted || 0}`} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={`Skipped: ${bulkUploadSummary.skipped || 0}`} />
                  </ListItem>
                </List>
                {Array.isArray(bulkUploadSummary.errors) && bulkUploadSummary.errors.length > 0 && (
                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Errors:
                    </Typography>
                    <List dense sx={{ maxHeight: 180, overflow: 'auto', border: '1px solid', borderColor: 'grey.200' }}>
                      {bulkUploadSummary.errors.map((err, index) => (
                        <ListItem key={`${err.row}-${index}`}>
                          <ListItemText
                            primary={`Row ${err.row}: ${err.message}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDialogClose} disabled={bulkUploading}>
            Close
          </Button>
          <Button
            onClick={handleBulkUpload}
            variant="contained"
            disabled={bulkUploading || !bulkUploadFile}
          >
            {bulkUploading ? 'Uploading...' : 'Upload Devices'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Assignment Dialog */}
      <Dialog open={bulkAssignDialog} onClose={handleBulkAssignDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Assign Devices to Schools</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Provide an Excel file with <strong>device_id</strong> and <strong>school_name</strong> columns.
            Devices that do not match a school or record will be skipped with detailed errors.
          </Typography>

          <Stack spacing={2} mt={2}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadAssignmentSample}
              disabled={bulkAssignUploading}
            >
              Download Assignment Sample
            </Button>

            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
              disabled={bulkAssignUploading}
            >
              {bulkAssignFile ? 'Change File' : 'Select Excel File'}
              <input
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={handleBulkAssignFileChange}
              />
            </Button>

            {bulkAssignFile && (
              <Alert severity="info">
                Selected file: <strong>{bulkAssignFile.name}</strong>
              </Alert>
            )}

            {bulkAssignUploading && <LinearProgress />}

            {bulkAssignError && (
              <Alert severity="error" onClose={() => setBulkAssignError('')}>
                {bulkAssignError}
              </Alert>
            )}

            {bulkAssignSummary && (
              <Alert severity="success">
                <Typography variant="subtitle2">Assignment Summary</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary={`Total Rows: ${bulkAssignSummary.total_rows || 0}`} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={`Assigned: ${bulkAssignSummary.assigned || 0}`} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={`Skipped: ${bulkAssignSummary.skipped || 0}`} />
                  </ListItem>
                </List>
                {Array.isArray(bulkAssignSummary.errors) && bulkAssignSummary.errors.length > 0 && (
                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Errors:
                    </Typography>
                    <List dense sx={{ maxHeight: 180, overflow: 'auto', border: '1px solid', borderColor: 'grey.200' }}>
                      {bulkAssignSummary.errors.map((err, index) => (
                        <ListItem key={`${err.row}-${index}`}>
                          <ListItemText primary={`Row ${err.row}: ${err.message}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkAssignDialogClose} disabled={bulkAssignUploading}>
            Close
          </Button>
          <Button
            onClick={handleBulkAssignmentUpload}
            variant="contained"
            disabled={bulkAssignUploading || !bulkAssignFile}
          >
            {bulkAssignUploading ? 'Assigning...' : 'Assign Devices'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderAssignToAgency = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography className="admin-page-title" variant="h5">
          Assign to Agency
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateAgency}
          >
            Add Agency
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadAgencyAssignmentSample}
          >
            Download Sample (Device ID only)
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => {
              resetBulkAssignToAgencyState();
              setBulkAssignToAgencyDialog(true);
            }}
          >
            Upload Excel for Bulk Assignment
          </Button>
        </Stack>
      </Box>

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Instructions
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          1. Download the sample Excel file to see the required format.
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          2. Select the agency from the dropdown when uploading.
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          3. Fill in device IDs only in the Excel file — use exact values from <strong>Admin → Devices</strong> (IMEI / Device ID column).
        </Typography>
        <Typography variant="body2" color="textSecondary">
          4. Upload the file to assign all listed devices to the selected agency.
        </Typography>
      </Card>

      <Card sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Available Agencies
          </Typography>
        </Box>
        {agencies.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No agencies found. Click "Add Agency" to create one.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Agency Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Commission Type</TableCell>
                  <TableCell>Commission %</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agencies.map((agency) => (
                  <TableRow key={agency.id}>
                    <TableCell>{agency.name}</TableCell>
                    <TableCell>{agency.email}</TableCell>
                    <TableCell>{agency.phone || 'N/A'}</TableCell>
                    <TableCell>{agency.contact_person || 'N/A'}</TableCell>
                    <TableCell>{agency.commission_type || 'percentage'}</TableCell>
                    <TableCell>{agency.sales_commission || 0}%</TableCell>
                    <TableCell>
                      <Chip
                        label={agency.status}
                        color={agency.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => handleEditAgency(agency)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Agency Create/Edit Dialog */}
      <Dialog open={agencyDialog} onClose={() => setAgencyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditingAgency ? 'Edit Agency' : 'Add New Agency'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Agency Name"
                value={agencyForm.name}
                onChange={(e) => setAgencyForm({...agencyForm, name: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={agencyForm.email}
                onChange={(e) => setAgencyForm({...agencyForm, email: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={agencyForm.phone}
                onChange={(e) => setAgencyForm({...agencyForm, phone: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={agencyForm.contact_person}
                onChange={(e) => setAgencyForm({...agencyForm, contact_person: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={agencyForm.address}
                onChange={(e) => setAgencyForm({...agencyForm, address: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sales Commission (%)"
                type="number"
                value={agencyForm.sales_commission}
                onChange={(e) => setAgencyForm({...agencyForm, commission_type: 'percentage', sales_commission: e.target.value})}
                helperText="Percentage only"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={agencyForm.status}
                  onChange={(e) => setAgencyForm({...agencyForm, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {isEditingAgency && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="New Password (leave blank to keep current)"
                  type="password"
                  value={agencyForm.password}
                  onChange={(e) => setAgencyForm({...agencyForm, password: e.target.value})}
                  autoComplete="new-password"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgencyDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveAgency} variant="contained">
            {isEditingAgency ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Assign to Agency Dialog */}
      <Dialog open={bulkAssignToAgencyDialog} onClose={handleBulkAssignToAgencyDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Assign Devices to Agency</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Agency</InputLabel>
              <Select
                value={bulkAssignToAgencyId}
                onChange={(e) => setBulkAssignToAgencyId(e.target.value)}
                label="Select Agency"
              >
                <MenuItem value="">
                  <em>Choose agency for this upload</em>
                </MenuItem>
                {agencies.map((agency) => (
                  <MenuItem key={agency.id} value={agency.id}>
                    {agency.name} ({agency.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="textSecondary" paragraph>
              Select the agency first, then upload Excel with <strong>Device ID</strong> column only.
              Use real device IDs from <strong>Admin → Devices</strong> (not placeholder names like GPS001).
              Agency name and email are not needed in the file — they come from the dropdown above.
              Devices already assigned to another agency will be rejected.
            </Typography>
            <Button
              variant="text"
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadAgencyAssignmentSample}
              sx={{ mb: 2 }}
            >
              Download sample Excel (Device ID only)
            </Button>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleBulkAssignToAgencyFileChange}
              style={{ display: 'none' }}
              id="bulk-assign-agency-file-input"
            />
            <label htmlFor="bulk-assign-agency-file-input">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 2 }}
              >
                {bulkAssignToAgencyFile ? bulkAssignToAgencyFile.name : 'Select Excel File'}
              </Button>
            </label>

            {bulkAssignToAgencyError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bulkAssignToAgencyError}
              </Alert>
            )}

            {bulkAssignToAgencySummary && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Agency: {bulkAssignToAgencySummary.agency_name || 'Selected agency'}
                </Typography>
                <Typography variant="body2">
                  Successfully assigned {bulkAssignToAgencySummary.successful_count || 0} devices.
                </Typography>
                {(bulkAssignToAgencySummary.skipped_count || 0) > 0 && (
                  <Typography variant="body2">
                    Skipped (already assigned): {bulkAssignToAgencySummary.skipped_count}
                  </Typography>
                )}
                {bulkAssignToAgencySummary.failed_count > 0 && (
                  <Typography variant="body2">
                    Failed: {bulkAssignToAgencySummary.failed_count} devices
                  </Typography>
                )}
                {bulkAssignToAgencySummary.errors && bulkAssignToAgencySummary.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Errors:
                    </Typography>
                    {bulkAssignToAgencySummary.errors.map((error, idx) => (
                      <Typography key={idx} variant="caption" display="block">
                        - {error.row || 'Unknown'}: {error.message}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {bulkAssignToAgencyUploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Uploading and processing...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkAssignToAgencyDialogClose} disabled={bulkAssignToAgencyUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssignToAgencyUpload}
            variant="contained"
            disabled={!bulkAssignToAgencyId || !bulkAssignToAgencyFile || bulkAssignToAgencyUploading}
          >
            {bulkAssignToAgencyUploading ? 'Uploading...' : 'Assign Devices'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const handleCreateUser = () => {
    setIsEditingUser(false);
    setUserForm({
      email: '',
      first_name: '',
      last_name: '',
      user_type: 'school',
      school_id: '',
      password: '',
      children_ids: []
    });
    setUserDialog(true);
  };

  const handleEditUser = (user) => {
    setIsEditingUser(true);
    setSelectedUser(user);
    setUserForm({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      user_type: user.user_type,
      school_id: user.school_id || '',
      password: '',
      children_ids: []
    });
    setUserDialog(true);
  };

  const handleDeleteUser = async (userToDelete) => {
    if (!userToDelete || !userToDelete.id) return;

    if (user && user.id && String(userToDelete.id) === String(user.id)) {
      alert('You cannot delete your own account while logged in.');
      return;
    }

    const displayName = `${userToDelete.first_name || ''} ${userToDelete.last_name || ''}`.trim() || userToDelete.email || 'this user';
    if (!window.confirm(`Are you sure you want to delete ${displayName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/admin/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        alert('User deleted successfully');
        await loadDashboardData();
        return;
      }

      let errorMessage = 'Failed to delete user';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (_) {
        // ignore json parse errors
      }
      alert(`Error: ${errorMessage}`);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Network error: ' + error.message);
    }
  };

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        return;
      }
      
      if (isEditingUser) {
        const response = await fetch(`${API_BASE_URL}/admin/users/${selectedUser.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(userForm)
        });
        
        if (!response.ok) {
          return;
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/admin/users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(userForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('User creation failed:', errorData);
          alert('Error: ' + (errorData.message || 'Failed to create user'));
          return;
        }
      }
      
      await loadDashboardData();
      setUserDialog(false);
    } catch (error) {
      console.error('Error in handleSaveUser:', error);
      alert('Network error: ' + error.message);
    }
  };
  
  const handleSchoolChange = (schoolId) => {
    setUserForm({...userForm, school_id: schoolId});
  };
  
  const handleUserTypeChange = (userType) => {
    setUserForm({...userForm, user_type: userType});
  };

  const filteredUsers = users.filter(user => {
    const matchesType = userFilter === 'all' ? true : user.user_type === userFilter;
    const matchesSearch = !userSearch || 
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      (user.school_name && user.school_name.toLowerCase().includes(userSearch.toLowerCase()));
    return matchesType && matchesSearch;
  });

  const filteredSchools = schools.filter(school =>
    school.name.toLowerCase().includes(schoolSearch.toLowerCase()) ||
    school.email?.toLowerCase().includes(schoolSearch.toLowerCase()) ||
    school.address?.toLowerCase().includes(schoolSearch.toLowerCase())
  );

  const filteredDevices = devices.filter(device => {
    const matchesSearch = !deviceSearch || 
      device.device_id.toLowerCase().includes(deviceSearch.toLowerCase()) ||
      (device.imei_number && device.imei_number.toLowerCase().includes(deviceSearch.toLowerCase())) ||
      (device.school_name && device.school_name.toLowerCase().includes(deviceSearch.toLowerCase()));
    const matchesType = deviceTypeFilter === 'all' || device.type === deviceTypeFilter;
    const matchesStatus = deviceStatusFilter === 'all' || device.assignment_status === deviceStatusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const renderUserManagement = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography className="admin-page-title" variant="h5">
          Users
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search users..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Filter by Role</InputLabel>
            <Select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              label="Filter by Role"
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="school">School Admins</MenuItem>
              <MenuItem value="parent">Parents</MenuItem>
              <MenuItem value="admin">Super Admins</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateUser}>
            Add User
          </Button>
        </Box>
      </Box>
      
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>User Type</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(filteredUsers) && filteredUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {u.first_name} {u.last_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={u.user_type}
                      color={u.user_type === 'admin' ? 'error' : u.user_type === 'school' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{u.school_name || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={u.status}
                      color={u.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditUser(u)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteUser(u)}
                      disabled={user && user.id && String(u.id) === String(user.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create/Edit User Dialog */}
      <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditingUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={userForm.first_name}
                onChange={(e) => setUserForm({...userForm, first_name: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={userForm.last_name}
                onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={userForm.user_type}
                  onChange={(e) => handleUserTypeChange(e.target.value)}
                  label="User Type"
                >
                  <MenuItem value="school">School Admin</MenuItem>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(userForm.user_type === 'school' || userForm.user_type === 'parent') && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>School</InputLabel>
                  <Select
                    value={userForm.school_id}
                    onChange={(e) => handleSchoolChange(e.target.value)}
                    label="School"
                  >
                    {schools.map((school) => (
                      <MenuItem key={school.id} value={school.id}>
                        {school.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isEditingUser ? "New Password (leave blank to keep current)" : "Password"}
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                required={!isEditingUser}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {isEditingUser ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const pageTitles = {
    overview: { title: 'Overview', subtitle: 'System snapshot' },
    schools: { title: 'Schools', subtitle: 'Create and manage schools' },
    users: { title: 'Users', subtitle: 'Admins, school logins and parents' },
    devices: { title: 'Device Inventory', subtitle: 'Add, upload and assign devices' },
    'device-debug-report': { title: 'Device Debug Report', subtitle: 'Assignment chain, connectivity and stability' },
    'device-complete-setup': { title: 'Complete Device Setup', subtitle: 'One-click server IP + GPS + contacts provisioning' },
    'marketing-assign': { title: 'Assign Test Device', subtitle: 'Create salesman parent login and assign device' },
    'assign-agency': { title: 'Assign to Agency', subtitle: 'Allocate devices to agencies' },
    billing: { title: 'Billing', subtitle: 'Revenue, subscriptions, commissions and withdrawals' },
    support: { title: 'Support', subtitle: 'Tickets and assistance' }
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  const goToMenu = (menu, options = {}) => {
    if (options.expand) {
      setExpandedMenus((p) => ({ ...p, [options.expand]: true }));
    }
    setActiveMenu(menu);
    closeMobileNav();
  };

  const toggleMenu = (key) => {
    setExpandedMenus((p) => ({ ...p, [key]: !p[key] }));
  };

  const renderPageContent = () => {
    switch (activeMenu) {
      case 'overview':
        return renderOverview();
      case 'schools':
        return renderSchoolManagement();
      case 'users':
        return renderUserManagement();
      case 'devices':
        return renderDeviceInventory();
      case 'device-debug-report':
        return <DeviceDebugReport schools={schools} agencies={agencies} />;
      case 'device-complete-setup':
        return <DeviceCompleteSetup />;
      case 'marketing-assign':
        return <MarketingDeviceAssign schools={schools} />;
      case 'assign-agency':
        return renderAssignToAgency();
      case 'billing':
        return <AdminBillingReports schools={schools} agencies={agencies} />;
      case 'support':
        return (
          <div className="admin-coming-soon">
            <Typography variant="h6" gutterBottom>Support Tickets</Typography>
            <Typography variant="body2">Coming soon</Typography>
          </div>
        );
      default:
        return renderOverview();
    }
  };

  // Don't render dashboard if no user or token
  const token = localStorage.getItem('authToken');
  if (!user || !token) {
    return (
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6">Please log in to access the dashboard</Typography>
      </Box>
    );
  }

  return (
    <div className={`admin-app ${mobileNavOpen ? 'nav-open' : ''}`}>
      {mobileNavOpen && <div className="admin-backdrop" onClick={closeMobileNav} aria-hidden="true" />}

      <aside className="admin-sidebar">
        <div className="admin-brand">
          <h1>Track My Child</h1>
          <p>Admin Portal</p>
        </div>

        <nav className="admin-nav" aria-label="Admin menu">
          <button
            type="button"
            className={`admin-nav-item ${activeMenu === 'overview' ? 'active' : ''}`}
            onClick={() => goToMenu('overview')}
          >
            Overview
          </button>
          <button
            type="button"
            className={`admin-nav-item ${activeMenu === 'schools' ? 'active' : ''}`}
            onClick={() => goToMenu('schools')}
          >
            Schools
          </button>
          <button
            type="button"
            className={`admin-nav-item ${activeMenu === 'users' ? 'active' : ''}`}
            onClick={() => goToMenu('users')}
          >
            Users
          </button>

          <button
            type="button"
            className={`admin-nav-parent ${expandedMenus.devices || activeMenu === 'devices' || activeMenu === 'assign-agency' || activeMenu === 'device-debug-report' || activeMenu === 'device-complete-setup' || activeMenu === 'marketing-assign' ? 'open' : ''}`}
            onClick={() => toggleMenu('devices')}
          >
            <span>Devices</span>
            <span className="admin-nav-caret">▼</span>
          </button>
          {expandedMenus.devices && (
            <div className="admin-nav-sub">
              <button
                type="button"
                className={`admin-nav-subitem ${activeMenu === 'devices' ? 'active' : ''}`}
                onClick={() => goToMenu('devices', { expand: 'devices' })}
              >
                Inventory
              </button>
              <button
                type="button"
                className={`admin-nav-subitem ${activeMenu === 'device-debug-report' ? 'active' : ''}`}
                onClick={() => goToMenu('device-debug-report', { expand: 'devices' })}
              >
                Debug Report
              </button>
              <button
                type="button"
                className={`admin-nav-subitem ${activeMenu === 'device-complete-setup' ? 'active' : ''}`}
                onClick={() => goToMenu('device-complete-setup', { expand: 'devices' })}
              >
                Complete Setup
              </button>
              <button
                type="button"
                className={`admin-nav-subitem ${activeMenu === 'marketing-assign' ? 'active' : ''}`}
                onClick={() => goToMenu('marketing-assign', { expand: 'devices' })}
              >
                Assign Test Device
              </button>
              <button
                type="button"
                className={`admin-nav-subitem ${activeMenu === 'assign-agency' ? 'active' : ''}`}
                onClick={() => goToMenu('assign-agency', { expand: 'devices' })}
              >
                Assign to Agency
              </button>
            </div>
          )}

          <button
            type="button"
            className={`admin-nav-item ${activeMenu === 'billing' ? 'active' : ''}`}
            onClick={() => goToMenu('billing')}
          >
            Billing
          </button>
          <button
            type="button"
            className={`admin-nav-item ${activeMenu === 'support' ? 'active' : ''}`}
            onClick={() => goToMenu('support')}
          >
            Support
          </button>
        </nav>

        <div className="admin-sidebar-footer">
          <button type="button" onClick={onLogout}>Logout</button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-left">
            <button
              type="button"
              className="admin-menu-toggle"
              onClick={() => setMobileNavOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              Menu
            </button>
            <div>
              <h2>{pageTitles[activeMenu]?.title || 'Admin'}</h2>
              <p>
                {pageTitles[activeMenu]?.subtitle}
                {user?.email ? ` · ${user.email}` : ''}
              </p>
            </div>
          </div>
        </header>

        <div className="admin-content">
          <div className="admin-panel">
            {renderPageContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;